'use client';
import type { ReactNode } from 'react';
import type { Document } from '@contentful/rich-text-types';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { VALIDITY } from '@/constants';
import { hasRichTextContent } from '@/packages/richText/utils';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import type { FlexibleFormVariant } from './FlexibleFormBase';
import { buildServerValidation, createFlexibleFormSubmitErrorValidation } from './flexibleFormShared';
import {
    getPromoCodeFieldErrorMessage,
    getPromoCodeFieldErrorText,
    getPromoCodeFormErrorMessage,
    type PromoCodeErrorState,
} from './promoCodeEntryValidation';
import { FlexibleFormAlert, type FlexibleFormAlertTone } from './FlexibleFormAlert';
import {
    getMicroservicesCountryCode,
    type MicroservicesCountryCode,
} from '@/services/microservices/usedCarEligibility';
import { submitPromoCodeValidationRequest } from '@/services/microservices/promoCode';
import { getTrialActivationBaseUrl } from '@/services/runtimeConfig';
import { buildCareRouteUrl } from '@/services/careUrls';

// Canada has no per-environment Care mapping today - kept as literal prod URLs, out of scope for MX-3456.
const PROMO_SUCCESS_URLS_CA = {
    campaign: 'https://care.siriusxm.ca/subscribe/checkout/purchase/streaming/organic/creds',
    default: 'https://care.siriusxm.ca/subscribe/checkout/purchase/streaming/organic',
} as const;

const PROMO_VIEW_OTHER_OFFERS_URL_CA = 'https://care.siriusxm.ca/subscribe/checkout/purchase/streaming/organic';

const STREAMING_ORGANIC_PATH = '/subscribe/checkout/purchase/streaming/organic';
const STREAMING_SELF_PAY_ORGANIC_PATH = '/subscribe/checkout/purchase/streaming/self-pay/organic';

const getRuntimeEnvironment = () =>
    process.env.SXM_ENV || process.env.npm_config_env || process.env.NEXT_PUBLIC_ENV || 'sandbox.preview';

function isProdEnvironment() {
    return getRuntimeEnvironment().toLowerCase().includes('prod');
}

function getNonProdPromoCheckoutBaseUrl() {
    const trialBaseUrl = getTrialActivationBaseUrl();

    if (!trialBaseUrl) {
        return buildCareRouteUrl('streamingSelfPayOrganic');
    }

    return trialBaseUrl.replace(/\/activate\/trial\/?$/, STREAMING_ORGANIC_PATH);
}

function getPromoSuccessUrls(countryCode: MicroservicesCountryCode) {
    if (countryCode === 'us') {
        return {
            campaign: buildCareRouteUrl('streamingOrganicCreds'),
            default: buildCareRouteUrl('streamingSelfPayOrganic'),
        };
    }

    if (isProdEnvironment()) {
        return PROMO_SUCCESS_URLS_CA;
    }

    const nonProdBaseUrl = getNonProdPromoCheckoutBaseUrl();
    return { campaign: nonProdBaseUrl, default: nonProdBaseUrl };
}

function getPromoViewOtherOffersUrl(countryCode: MicroservicesCountryCode) {
    if (countryCode === 'us') {
        return buildCareRouteUrl('streamingSelfPayOrganic');
    }

    if (isProdEnvironment()) {
        return PROMO_VIEW_OTHER_OFFERS_URL_CA;
    }

    const nonProdCheckoutBaseUrl = getNonProdPromoCheckoutBaseUrl();
    const viewOtherOffersUrl = new URL(nonProdCheckoutBaseUrl);
    viewOtherOffersUrl.pathname = viewOtherOffersUrl.pathname.replace(
        STREAMING_ORGANIC_PATH,
        STREAMING_SELF_PAY_ORGANIC_PATH,
    );

    return viewOtherOffersUrl.toString();
}

interface PromoCodeValidationError {
    errorCode?: string;
    fieldErrors?: Array<{
        errorCode?: string;
    }>;
}

function getCaseInsensitiveSearchParam(searchParams: URLSearchParams, targetKey: string) {
    const normalizedTargetKey = targetKey.toLowerCase();

    for (const [key, value] of searchParams.entries()) {
        if (key.toLowerCase() === normalizedTargetKey) {
            return value;
        }
    }

    return undefined;
}

function deleteCaseInsensitiveSearchParam(searchParams: URLSearchParams, targetKey: string) {
    const normalizedTargetKey = targetKey.toLowerCase();
    const keysToDelete = Array.from(searchParams.keys()).filter((key) => key.toLowerCase() === normalizedTargetKey);

    keysToDelete.forEach((key) => {
        searchParams.delete(key);
    });
}

function copyCurrentQueryParams(targetUrl: URL, currentUrl: URL) {
    currentUrl.searchParams.forEach((value, key) => {
        targetUrl.searchParams.append(key, value);
    });
}

function buildPromoCodeViewOtherOffersUrl(countryCode: MicroservicesCountryCode) {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(getPromoViewOtherOffersUrl(countryCode));
    copyCurrentQueryParams(targetUrl, currentUrl);
    return targetUrl.toString();
}

function buildPromoCodeRedirectUrl(
    countryCode: MicroservicesCountryCode,
    promoCode: string,
    smsOffer: boolean | undefined,
) {
    const promoSuccessUrls = getPromoSuccessUrls(countryCode);
    const currentUrl = new URL(window.location.href);
    const campaign = getCaseInsensitiveSearchParam(currentUrl.searchParams, 'campaign');
    const hasCampaign = campaign !== undefined;
    const langpref = getCaseInsensitiveSearchParam(currentUrl.searchParams, 'langpref');
    const targetUrl = new URL(hasCampaign ? promoSuccessUrls.campaign : promoSuccessUrls.default);

    copyCurrentQueryParams(targetUrl, currentUrl);

    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'promocode');
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'campaign');
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'langpref');
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, 'smsOffer');

    targetUrl.searchParams.set('promocode', promoCode);

    if (hasCampaign) {
        targetUrl.searchParams.set('campaign', campaign || '');
    } else {
        targetUrl.searchParams.set('smsOffer', String(Boolean(smsOffer)));
    }

    if (langpref !== undefined) {
        targetUrl.searchParams.set('langpref', langpref);
    }

    return targetUrl.toString();
}

const DEFAULT_PROMO_CODE_PARAM = 'promocode';

// The authored redirect names the param the code goes in by leaving it empty, e.g. `/offers/x?promocode=`.
function getPromoCodeParamName(redirectUrl: URL) {
    for (const [key, value] of redirectUrl.searchParams.entries()) {
        if (value === '') {
            return key;
        }
    }

    return DEFAULT_PROMO_CODE_PARAM;
}

// Success Page Redirect (e.g. a partner offers page) replaces the Care checkout destination; the
// target page's plan CTAs inherit the code from the URL via useInheritParams (URL_INHERIT_PARAMS).
function buildPromoCodeSuccessPageRedirectUrl(successPageRedirectHref: string, promoCode: string) {
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(successPageRedirectHref, currentUrl.origin);
    const promoCodeParamName = getPromoCodeParamName(targetUrl);

    copyCurrentQueryParams(targetUrl, currentUrl);
    deleteCaseInsensitiveSearchParam(targetUrl.searchParams, promoCodeParamName);
    targetUrl.searchParams.set(promoCodeParamName, promoCode);

    return targetUrl.toString();
}

function normalizeErrorCode(errorCode?: string) {
    return `${errorCode || ''}`.toUpperCase();
}

function getPromoCodeErrorCodes(error: PromoCodeValidationError | undefined) {
    return [error?.errorCode, ...(error?.fieldErrors || []).map((fieldError) => fieldError.errorCode)]
        .map(normalizeErrorCode)
        .filter(Boolean);
}

function getPromoCodeErrorState(errorCodes: string[]): PromoCodeErrorState | undefined {
    if (errorCodes.some((errorCode) => errorCode.includes('ALREADY_REDEEMED'))) {
        return 'redeemed';
    }

    if (errorCodes.some((errorCode) => errorCode.includes('OUTSIDE_START_AND_END_DATE'))) {
        return 'expired';
    }

    if (errorCodes.length > 0) {
        return 'invalid';
    }

    return undefined;
}

function isPromoCodeValidationError(error: unknown): error is PromoCodeValidationError {
    return typeof error === 'object' && error !== null && ('errorCode' in error || 'fieldErrors' in error);
}

// Browser-side validation reports its own user-error from FlexibleFormBase; a code the service
// rejects only becomes known here, so it reports the message the user was shown.
function trackPromoCodeFieldError(errorName: string, errorCode: string) {
    (window as BrowserWindow).pushToAdobe({
        event: 'user-error',
        errorInfo: [{ errorType: 'USER', errorName, errorCode }],
    });
}

function setPromoCodeFieldError(
    promoCodeFieldName: string,
    promoCodeErrorState: PromoCodeErrorState,
    viewOtherOffersUrl: string,
    invalidCodeMessage: ReactNode | undefined,
    invalidCodeMessageText: string,
    errorCode: string,
    context: Parameters<Exclude<FlexibleFormVariant['customSubmit'], undefined>>[1],
) {
    const { validations, setValidations, setSubmitState } = context;
    const useAuthoredMessage = promoCodeErrorState === 'invalid' && Boolean(invalidCodeMessage);
    const serverValidation = buildServerValidation(validations, promoCodeFieldName);
    const promoCodeFieldMessage = useAuthoredMessage
        ? invalidCodeMessage
        : getPromoCodeFieldErrorMessage(promoCodeErrorState, viewOtherOffersUrl);
    const tone: FlexibleFormAlertTone = promoCodeErrorState === 'invalid' ? 'critical' : 'warning';
    const alert = <FlexibleFormAlert tone={tone} message={promoCodeFieldMessage} />;

    trackPromoCodeFieldError(
        useAuthoredMessage ? invalidCodeMessageText : getPromoCodeFieldErrorText(promoCodeErrorState),
        errorCode,
    );

    if (!serverValidation) {
        setValidations(createFlexibleFormSubmitErrorValidation(validations, promoCodeFieldMessage, tone));
        setSubmitState('untouched');
        return;
    }

    setValidations({
        ...validations,
        [promoCodeFieldName]: {
            ...serverValidation,
            validations: { ...serverValidation.validations, regex: false },
            helperText: { ...serverValidation.helperText, regex: '' },
        },
        form: {
            value: '',
            invalid: VALIDITY.invalid,
            validations: { ...validations.form.validations, formerror: true },
            helperText: { ...validations.form.helperText, formerror: alert },
        },
    });
    setSubmitState('untouched');
}

function setPromoCodeFormError(
    viewOtherOffersUrl: string,
    context: Parameters<Exclude<FlexibleFormVariant['customSubmit'], undefined>>[1],
) {
    const { validations, setValidations, setSubmitState } = context;

    setValidations(
        createFlexibleFormSubmitErrorValidation(validations, getPromoCodeFormErrorMessage(viewOtherOffersUrl)),
    );
    setSubmitState('untouched');
}

interface PromoCodeCustomSubmitOptions {
    successPageRedirectHref?: string;
    invalidCodeMessage?: Document;
}

export function createPromoCodeCustomSubmit(
    promoCodeFieldName?: string,
    { successPageRedirectHref, invalidCodeMessage }: PromoCodeCustomSubmitOptions = {},
): FlexibleFormVariant['customSubmit'] {
    const hasAuthoredInvalidCodeMessage = invalidCodeMessage && hasRichTextContent(invalidCodeMessage);
    const authoredInvalidCodeMessage = hasAuthoredInvalidCodeMessage ? (
        <RenderRTE contentfulDoc={invalidCodeMessage} />
    ) : undefined;
    const authoredInvalidCodeMessageText = hasAuthoredInvalidCodeMessage
        ? documentToPlainTextString(invalidCodeMessage).trim()
        : '';

    return async (data, context) => {
        const countryCode = getMicroservicesCountryCode();
        const viewOtherOffersUrl = buildPromoCodeViewOtherOffersUrl(countryCode);

        if (!promoCodeFieldName) {
            setPromoCodeFormError(viewOtherOffersUrl, context);
            return;
        }

        const promoCode = `${(data.get(promoCodeFieldName) as string) || ''}`.trim();

        try {
            const response = await submitPromoCodeValidationRequest(countryCode, {
                marketingPromoCode: promoCode,
                streaming: true,
            });

            if (response.status !== 'SUCCESS') {
                setPromoCodeFormError(viewOtherOffersUrl, context);
                return;
            }

            window.location.assign(
                successPageRedirectHref
                    ? buildPromoCodeSuccessPageRedirectUrl(successPageRedirectHref, promoCode)
                    : buildPromoCodeRedirectUrl(countryCode, promoCode, response.data?.smsOffer),
            );
        } catch (error) {
            const errorCodes = getPromoCodeErrorCodes(isPromoCodeValidationError(error) ? error : undefined);
            const promoCodeErrorState = getPromoCodeErrorState(errorCodes);

            if (promoCodeErrorState) {
                setPromoCodeFieldError(
                    promoCodeFieldName,
                    promoCodeErrorState,
                    viewOtherOffersUrl,
                    authoredInvalidCodeMessage,
                    authoredInvalidCodeMessageText,
                    errorCodes[0],
                    context,
                );
                return;
            }

            setPromoCodeFormError(viewOtherOffersUrl, context);
        }
    };
}
